import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Trash2, Loader2, Users, ChevronDown } from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';
import { useUser } from '@/hooks/useUser';
import type { OrgMemberRole } from '@/types/database';

interface Member {
  id: string;
  profile_id: string;
  org_id: string;
  role: OrgMemberRole;
  created_at: string;
  profile: {
    id: string;
    display_name: string | null;
    avatar_url: string | null;
  } | null;
}

interface OrgMembersPanelProps {
  orgId: string;
}

const ROLE_OPTIONS: OrgMemberRole[] = [
  'ORG_OWNER',
  'WORKSPACE_ADMIN',
  'MEMBER',
];

async function fetchMembers(orgId: string): Promise<Member[]> {
  const res = await apiFetch(`/api/organizations/${orgId}/members`);
  if (!res.ok) throw new Error(`Failed to load members: ${res.status}`);
  const json = await res.json();
  return (json.data ?? []) as Member[];
}

async function resolveUserByEmail(
  email: string
): Promise<{ id: string; display_name: string } | null> {
  const res = await apiFetch(`/api/users?search=${encodeURIComponent(email)}`);
  if (!res.ok) return null;
  const json = await res.json();
  const data = Array.isArray(json) ? json : (json.data ?? []);
  const user = data.find(
    (u: { email?: string }) => u.email?.toLowerCase() === email.toLowerCase()
  );
  if (!user) return null;
  return {
    id: user.id,
    display_name:
      [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email,
  };
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export const OrgMembersPanel: React.FC<OrgMembersPanelProps> = ({ orgId }) => {
  const { user } = useUser();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [resolvedUser, setResolvedUser] = useState<{
    id: string;
    display_name: string;
  } | null>(null);
  const [newRole, setNewRole] = useState<OrgMemberRole>('MEMBER');
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Create-user form state
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [autoConfirm, setAutoConfirm] = useState(true);
  const [newUserRole, setNewUserRole] = useState<OrgMemberRole>('MEMBER');
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const canManage = user?.role === 'SYSTEM_ADMIN' || user?.role === 'ORG_OWNER';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMembers(orgId);
      setMembers(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openAddDialog = () => {
    setEmail('');
    setResolvedUser(null);
    setNewRole('MEMBER');
    setActionError(null);
    setAddOpen(true);
  };

  const handleEmailBlur = async () => {
    if (!email.trim()) {
      setResolvedUser(null);
      return;
    }
    const user = await resolveUserByEmail(email.trim());
    setResolvedUser(user);
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setActionError('Email is required');
      return;
    }
    if (!resolvedUser) {
      setActionError('No user found with that email');
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      const res = await apiFetch(`/api/organizations/${orgId}/members`, {
        method: 'POST',
        body: JSON.stringify({
          profile_id: resolvedUser.id,
          role: newRole,
        }),
      });
      if (!res.ok) throw new Error('Failed to add member');
      setAddOpen(false);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to add member');
    } finally {
      setSaving(false);
    }
  };

  const openCreateDialog = () => {
    setNewEmail('');
    setNewPassword('');
    setNewDisplayName('');
    setAutoConfirm(true);
    setNewUserRole('MEMBER');
    setCreateError(null);
    setCreateOpen(true);
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim() || !newPassword.trim()) {
      setCreateError('Email and password are required');
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    try {
      const res = await apiFetch('/api/admin/create-user', {
        method: 'POST',
        body: JSON.stringify({
          email: newEmail.trim(),
          password: newPassword,
          organization_id: orgId,
          display_name: newDisplayName.trim() || undefined,
          role: newUserRole,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          (body as { error?: string }).error ?? 'Failed to create user'
        );
      }
      setCreateOpen(false);
      await load();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Failed to create user');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleRemove = async (profileId: string) => {
    try {
      const res = await apiFetch(
        `/api/organizations/${orgId}/members/${profileId}`,
        {
          method: 'DELETE',
        }
      );
      if (!res.ok) throw new Error('Failed to remove member');
      await load();
    } catch {
      // silent
    }
  };

  const handleRoleChange = async (profileId: string, role: OrgMemberRole) => {
    try {
      const res = await apiFetch(
        `/api/organizations/${orgId}/members/${profileId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ role }),
        }
      );
      if (!res.ok) throw new Error('Failed to update role');
      await load();
    } catch {
      // silent
    }
  };

  const roleVariant = (
    role: OrgMemberRole
  ): 'default' | 'secondary' | 'outline' => {
    switch (role) {
      case 'ORG_OWNER':
        return 'default';
      case 'WORKSPACE_ADMIN':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Members
            </CardTitle>
            <CardDescription>
              Manage who has access to this organization.
            </CardDescription>
          </div>
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Member
                  <ChevronDown className="w-3 h-3 ml-1 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={openAddDialog}>
                  Send Invitation
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openCreateDialog}>
                  Create New User
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* ── Send Invitation dialog ── */}
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <span />
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Send Invitation</DialogTitle>
                <DialogDescription>
                  Search for an existing user by email and add them to the
                  organization.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAdd} className="space-y-4 py-2">
                <div className="space-y-1">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setResolvedUser(null);
                    }}
                    onBlur={handleEmailBlur}
                    placeholder="user@example.com"
                    autoFocus
                  />
                  {resolvedUser && (
                    <p className="text-xs text-green-600">
                      Found: {resolvedUser.display_name}
                    </p>
                  )}
                  {email && !resolvedUser && (
                    <p className="text-xs text-amber-600">
                      Enter a valid email and tab away to verify
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label>Role</Label>
                  <Select
                    value={newRole}
                    onValueChange={(v) => setNewRole(v as OrgMemberRole)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r.replace(/_/g, ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {actionError && (
                  <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                    {actionError}
                  </p>
                )}
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAddOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving || !resolvedUser}>
                    {saving && (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    )}
                    Send
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* ── Create New User dialog ── */}
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <span />
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>
                  Create a new user account with auto-confirmed email. No
                  confirmation email will be sent.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4 py-2">
                <div className="space-y-1">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="user@example.com"
                    autoFocus
                  />
                </div>
                <div className="space-y-1">
                  <Label>Display name</Label>
                  <Input
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    placeholder="First Last (optional)"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoConfirm}
                    onChange={(e) => setAutoConfirm(e.target.checked)}
                    className="rounded border-gray-400 text-blue-600 focus:ring-blue-500"
                  />
                  <Label>Auto-confirm email</Label>
                </label>
                <p className="text-xs text-gray-500">
                  A confirmation email will not be sent when creating a user via
                  this form.
                </p>
                <div className="space-y-1">
                  <Label>Role</Label>
                  <Select
                    value={newUserRole}
                    onValueChange={(v) => setNewUserRole(v as OrgMemberRole)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r.replace(/_/g, ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {createError && (
                  <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                    {createError}
                  </p>
                )}
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCreateOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createSaving}>
                    {createSaving && (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    )}
                    Create User
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {error && (
          <div className="border-b border-red-200 bg-red-50 px-6 py-3 text-sm text-red-600">
            {error}
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading members…
          </div>
        ) : members.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>No members yet.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="font-medium">
                    {m.profile?.display_name ?? (
                      <span className="font-mono text-xs text-gray-400">
                        {m.profile_id}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={m.role}
                      onValueChange={(v) =>
                        handleRoleChange(m.profile_id, v as OrgMemberRole)
                      }
                    >
                      <SelectTrigger className="w-40 h-8 text-xs">
                        <SelectValue>
                          <Badge
                            variant={roleVariant(m.role)}
                            className="text-xs"
                          >
                            {m.role.replace(/_/g, ' ')}
                          </Badge>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {ROLE_OPTIONS.map((r) => (
                          <SelectItem key={r} value={r}>
                            {r.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {formatDate(m.created_at)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-400 hover:text-red-600"
                      title="Remove member"
                      onClick={() => handleRemove(m.profile_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
